/**
 * データ参照・レコード単位編集画面 (Full版)
 * 設計: docs/design/v03-record-level-data-editing-detail.md §8
 *
 * 従来は読み取り専用 (絞込表示 + CSVエクスポート) だった画面に、行の追加・修正・削除・一括削除を載せた。
 * 旧実装はネイティブ select・タップ領域 30px で UI 完了条件 (CLAUDE.md §9) を満たしていなかったため、
 * 部分改修ではなく作り直している。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Database, Filter, Search, Plus, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { DATA_TYPE_SPECS } from '@/lib/import-specs';
import { fieldsOf, formatCell, CARD_LAYOUT } from '@/lib/data-record-fields';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { ConfirmDialog } from '@/components/Modal';
import { RecordEditModal, PublishImpactWarning, type RecordRow } from '@/components/data/RecordEditModal';
import { EmptyState } from '@/components/EmptyState';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { csvEscape as csvEscapeShared } from '@/lib/csv-export';

/** data_type → 書込みに必要な permission feature。
 *  ⚠️ apps/api/src/routes/data-browse.ts の READ_FEATURES と同じ対応を保つこと。
 *     片方だけ更新すると「押せるが必ず 403」または「権限があるのに押せない」になる (§8-55 型)。 */
const WRITE_FEATURES: Record<string, string[]> = {
  sales: ['manual_entry_sales', 'import'],
  delivery_details: ['manual_entry_delivery_details', 'import'],
  delivery_sales: ['import'],
  labor: ['import'],
  opex: ['import'],
};

type Store = { store_id: string; store_name: string; store_code: string };
type SummaryRow = { data_type: string; store_code: string; month: string; cnt: number };
type RecordsResponse = {
  data_type: string;
  store_id: string | null;
  month: string | null;
  count: number;
  total_count: number;
  is_editable: boolean;
  rows: RecordRow[];
  csv_headers: string[];
  csv_filename: string;
};

const dataTypeList = Object.values(DATA_TYPE_SPECS);
const PAGE_SIZE = 100;

/** 単一削除の確認ダイアログに出す識別用フィールド。
 *  種別ごとに定義せず「あれば出す」方式にしているのは、5種すべてでこの中の2〜4個が該当するため。 */
const DELETE_CONFIRM_FIELDS = [
  'store_code', 'menu_code', 'service_code', 'ingredient_code', 'item_name',
  'sales_month', 'target_month', 'delivery_date', 'amount', 'sales_amount', 'labor_cost_amount',
];

const monthsBackList = (n: number): string[] => {
  const list: string[] = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    list.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return list;
};

/** 'YYYY-MM' の月末日を 'YYYY-MM-DD' で返す */
const lastDayOfMonth = (yearMonth: string): string => {
  const [y, m] = yearMonth.split('-').map(Number);
  return `${yearMonth}-${String(new Date(y!, m!, 0).getDate()).padStart(2, '0')}`;
};

/** ⚠️ このエクスポートは CSV_HEADERS が import-specs.ts と一致しており、そのまま取込へ戻せる
 *  「往復用」CSV。したがって formula injection 対策の先頭クォート前置は**行わない**
 *  (`-500` が `'-500` になると再取込で数値として読めなくなる、§8-67)。
 *  共有ヘルパーを escapeFormulas=false で呼ぶことでこの意図を明示する
 *  (ローカルに同名関数を再定義していると、次の改修で lib 側だけ直されて挙動が割れる)。 */
const csvEscape = (v: unknown) => csvEscapeShared(v, false);

/** '2026-01' → '2026年1月' */
const jpMonth = (m: string) => {
  const [y, mo] = m.split('-');
  return y && mo ? `${y}年${Number(mo)}月` : m;
};

/** 行から公開ゲートの影響期間を導く (表示用) */
const periodOfRow = (dataType: string, row: RecordRow): string | null => {
  const raw = dataType === 'delivery_details' ? row.delivery_date
    : (dataType === 'sales' || dataType === 'delivery_sales') ? row.sales_month
      : row.target_month;
  if (!raw) return null;
  return jpMonth(String(raw).slice(0, 7).replace(/\//g, '-'));
};

export function DataBrowsePage() {
  const qc = useQueryClient();
  // 手入力画面からの導線 (/data?data_type=delivery_details 等) を受け取る。
  // 未知の値が来ても落とさず既定値へフォールバックする。
  const [searchParams] = useSearchParams();
  const initialDataType = DATA_TYPE_SPECS[searchParams.get('data_type') ?? ''] ? searchParams.get('data_type')! : 'sales';
  const [dataType, setDataType] = useState(initialDataType);
  const [storeId, setStoreId] = useState('');
  // 期間指定 (2026-08-17、実運用フィードバック: 日付を指定しないと何があるか分からずメンテしづらい)。
  // 月セレクトは「よく使う期間のプリセット」として残し、実体は from/to の2値に一本化する
  // (絞り込みの真実の源を1つにしないと、月と期間のどちらが効いているのか画面から読めなくなる)。
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editTarget, setEditTarget] = useState<{ mode: 'create' | 'edit'; row: RecordRow } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RecordRow | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [liveMessage, setLiveMessage] = useState('');

  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });

  const { data: summary = { summary: [] as SummaryRow[] } } = useQuery<{ summary: SummaryRow[] }>({
    queryKey: ['data-summary'],
    queryFn: () => apiFetch<{ summary: SummaryRow[] }>('/data/summary'),
  });

  const buildParams = (opts?: { forExport?: boolean }) => {
    const p = new URLSearchParams({ data_type: dataType });
    if (storeId) p.append('store_id', storeId);
    if (from) p.append('from', from);
    if (to) p.append('to', to);
    if (q) p.append('q', q);
    if (opts?.forExport) p.append('export', '1');
    else { p.append('limit', String(PAGE_SIZE)); p.append('offset', String(page * PAGE_SIZE)); }
    return p;
  };

  const records = useQuery<RecordsResponse>({
    queryKey: ['data-records', dataType, storeId, from, to, q, page],
    queryFn: () => apiFetch<RecordsResponse>(`/data/records?${buildParams()}`),
  });

  const fields = useMemo(() => fieldsOf(dataType), [dataType]);
  const spec = DATA_TYPE_SPECS[dataType];
  const rows = records.data?.rows ?? [];
  const total = records.data?.total_count ?? 0;
  // ⚠️ is_editable は「データ種別として編集可能か」しか見ていない。書込み権限まで確認しないと、
  //    import:view しか持たない店長 (STATIC_MANAGER_PERMISSIONS 経由) に操作ボタンが出て、
  //    押すと必ず 403 になる (S-11 の QA レビューで検出)。バックエンドの
  //    data_type → feature マップ (READ_FEATURES) と同じ OR 条件でフロントも判定する。
  const perms = useAuthStore((s) => s.user?.effective_permissions);
  const canWrite = (records.data?.is_editable ?? false)
    && (WRITE_FEATURES[dataType] ?? []).some((f) => hasPermission(perms, f, 'update'));
  const isEditable = canWrite;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // ⚠️ 選択状態の規約 (Design CRITICAL-2):
  //    ページ送りをまたいだ選択は保持するが、絞込条件・データ種別を変えたら破棄する。
  //    黙って持ち越すと「選択したつもりのない行を消す」事故になるため、破棄はトーストで知らせる。
  const filterKey = `${dataType}|${storeId}|${from}|${to}|${q}`;
  const prevFilterKey = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKey.current === filterKey) return;
    prevFilterKey.current = filterKey;
    setPage(0);
    setSelected((prev) => {
      if (prev.size > 0) toast.info(`絞り込みを変更したため、選択していた ${prev.size} 件を解除しました`);
      return new Set();
    });
  }, [filterKey]);

  useEffect(() => {
    if (!records.data) return;
    setLiveMessage(`${total.toLocaleString()} 件中 ${rows.length.toLocaleString()} 件を表示しています`);
  }, [records.data, total, rows.length]);

  const summaryMatrix = useMemo(() => {
    const byDt = new Map<string, Map<string, Map<string, number>>>();
    for (const s of summary.summary ?? []) {
      if (!byDt.has(s.data_type)) byDt.set(s.data_type, new Map());
      const byStore = byDt.get(s.data_type)!;
      if (!byStore.has(s.store_code)) byStore.set(s.store_code, new Map());
      byStore.get(s.store_code)!.set(s.month, s.cnt);
    }
    return byDt;
  }, [summary]);

  const allMonths = useMemo(() => {
    const set = new Set<string>();
    for (const s of summary.summary ?? []) set.add(s.month);
    return [...set].sort();
  }, [summary]);

  /** 期間プリセット。選択中の from/to がどのプリセットと一致するかを逆算して表示に反映する
   *  (プリセットで選んでから開始日だけ直した場合に、セレクトの表示が実態とズレないようにするため)。 */
  /** プリセット名 → [開始日, 終了日]。任意の期間は日付欄で直接指定する。 */
  const presetRange = (v: string): [string, string] | null => {
    const months = monthsBackList(12);
    switch (v) {
      case '__thisMonth__': return [`${months[0]!}-01`, lastDayOfMonth(months[0]!)];
      case '__lastMonth__': return [`${months[1]!}-01`, lastDayOfMonth(months[1]!)];
      case '__last3__': return [`${months[2]!}-01`, lastDayOfMonth(months[0]!)];
      case '__last6__': return [`${months[5]!}-01`, lastDayOfMonth(months[0]!)];
      case '__last12__': return [`${months[11]!}-01`, lastDayOfMonth(months[0]!)];
      default: return null;
    }
  };

  /** 現在の from/to がどのプリセットに一致するかを逆算する。
   *  日付欄を直接いじった場合はどれにも一致せず「__custom__」になり、セレクトは空表示になる
   *  (実態と違うプリセット名が出たままにならないようにするため)。 */
  const activePreset = useMemo(() => {
    if (!from && !to) return '__all__';
    for (const v of ['__thisMonth__', '__lastMonth__', '__last3__', '__last6__', '__last12__']) {
      const r = presetRange(v);
      if (r && from === r[0] && to === r[1]) return v;
    }
    return '__custom__';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const applyPreset = (v: string) => {
    if (v === '__all__') { setFrom(''); setTo(''); return; }
    const r = presetRange(v);
    if (r) { setFrom(r[0]); setTo(r[1]); }
  };

  /** 行追加時の初期値。絞り込みの開始日が入っていれば、その月/日を対象期間の初期値として引き継ぐ。
   *  data_type ごとに期間を表す列名が違うため data-record-fields の対応表と揃える。 */
  const defaultPeriodFields = (): Record<string, unknown> => {
    if (!from) return {};
    const monthOf = from.slice(0, 7);
    switch (dataType) {
      case 'sales': return { sales_month: monthOf, period_start: from, period_end: to || lastDayOfMonth(monthOf) };
      case 'delivery_sales': return { sales_month: monthOf };
      case 'delivery_details': return { delivery_date: from };
      case 'labor':
      case 'opex': return { target_month: monthOf };
      default: return {};
    }
  };

  /** data_type → 対応する手入力画面。まとめて入力したいときはそちらへ誘導する (設計 §5-3)。 */
  const entryPathOf = (dt: string): string | null => ({
    sales: '/entry/sales',
    delivery_details: '/entry/delivery-details',
    delivery_sales: '/entry/delivery-sales',
    labor: '/entry/labor',
    opex: '/entry/opex',
  } as Record<string, string>)[dt] ?? null;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['data-records'] });
    qc.invalidateQueries({ queryKey: ['data-summary'] });
  };

  const downloadCsv = async () => {
    try {
      const all = await apiFetch<RecordsResponse>(`/data/records?${buildParams({ forExport: true })}`);
      const lines = [all.csv_headers.join(',')];
      for (const r of all.rows) lines.push(all.csv_headers.map((h) => csvEscape(r[h])).join(','));
      const blob = new Blob(['﻿' + lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = all.csv_filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${all.rows.length.toLocaleString()} 件をダウンロードしました`);
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : 'CSVの出力に失敗しました');
    }
  };

  const submitRecord = async (values: Record<string, string>, changedNames: string[]) => {
    if (!editTarget) return;
    setSaving(true); setModalError(null);
    try {
      // 空文字は「未入力」として送らない (数値項目が 0 に化けるのを防ぐ)
      const payload: Record<string, string> = {};
      for (const [k, v] of Object.entries(values)) if (v !== '') payload[k] = v;

      if (editTarget.mode === 'create') {
        const targetStore = storeId || String(editTarget.row.store_id ?? '');
        if (!targetStore) { setModalError('店舗を選択してください'); setSaving(false); return; }
        const res = await apiFetch<{ record_id: string }>('/data/records', {
          method: 'POST',
          body: JSON.stringify({ data_type: dataType, store_id: targetStore, values: payload }),
        });
        setHighlightId(res.record_id);
        toast.success('データを追加しました');
      } else {
        // ⚠️ 修正では「実際に値が変わった項目だけ」を送る (changedNames はモーダルが算出)。
        //    フォームの全項目を送ると、触っていない項目まで「明示指定」としてサーバへ届き、
        //    §3-3 の派生値ルール (amount/quantity 変更時の unit_price 追随、食材未変更時の
        //    コスト分類スナップショット維持) が丸ごと無効化される
        //    (S-11 レビューで Test / design-and-code が独立検出)。
        const diff: Record<string, string> = {};
        for (const name of changedNames) if (name in payload) diff[name] = payload[name]!;
        if (Object.keys(diff).length === 0) {
          setEditTarget(null); setSaving(false);
          return;
        }
        const res = await apiFetch<{ record_id: string }>(`/data/records/${editTarget.row.record_id}`, {
          method: 'PATCH',
          body: JSON.stringify({ data_type: dataType, version: editTarget.row.version, values: diff }),
        });
        setHighlightId(res.record_id);
        toast.success('データを修正しました');
      }
      setEditTarget(null);
      refresh();
    } catch (e) {
      setModalError(e instanceof ApiClientError ? e.message : '保存に失敗しました');
      // ⚠️ 楽観ロック競合はモーダルを開いたままだと永久に抜けられない。
      //    保持している version が古いままなので、何度保存を押しても 409 が続く。
      //    一覧を取り直したうえでモーダルを閉じ、最新値から編集し直してもらう (S-11 QA/Design 指摘)。
      if (e instanceof ApiClientError && e.code === 'VERSION_CONFLICT') {
        refresh();
        setEditTarget(null);
        toast.error('他のユーザーがこのデータを更新しました。最新の内容を読み込みました。もう一度編集してください');
      }
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await apiFetch(`/data/records/${deleteTarget.record_id}?data_type=${dataType}`, { method: 'DELETE' });
      toast.success('データを削除しました');
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const doBulkDelete = async () => {
    setSaving(true);
    try {
      const res = await apiFetch<{ requested: number; deleted: number }>('/data/records/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ data_type: dataType, record_ids: [...selected] }),
      });
      if (res.deleted < res.requested) {
        toast.info(`${res.deleted} 件を削除しました（${res.requested - res.deleted} 件は対象外でした）`);
      } else {
        toast.success(`${res.deleted} 件を削除しました`);
      }
      setSelected(new Set());
      setBulkConfirm(false);
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const pageIds = rows.map((r) => String(r.record_id ?? ''));
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someOnPageSelected = pageIds.some((id) => selected.has(id));
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someOnPageSelected && !allOnPageSelected;
  }, [someOnPageSelected, allOnPageSelected]);

  const toggleAllOnPage = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    return next;
  });

  const selectedRowsOnPage = rows.filter((r) => selected.has(String(r.record_id)));
  const bulkPeriods = [...new Set(selectedRowsOnPage.map((r) => periodOfRow(dataType, r)).filter((v): v is string => !!v))];
  const storeLabel = storeId
    ? (stores.find((s) => s.store_id === storeId)?.store_name ?? storeId)
    : '（店舗を選択してください）';

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <h1 className="text-h1">取込済みデータ</h1>
        <p className="text-sm text-text-muted mt-1">
          店舗・期間・データ種別で絞り込み、名称やコードで検索して、行ごとに追加・修正・削除ができます
        </p>
        {entryPathOf(dataType) && (
          <p className="text-sm text-text-muted mt-2 flex items-center gap-1.5 flex-wrap">
            <Pencil size={14} aria-hidden className="shrink-0" />
            まとめて入力する場合は
            <Link to={entryPathOf(dataType)!} className="font-bold text-chrome-base underline hover:no-underline">
              {spec?.label ?? ''}の手入力
            </Link>
            をお使いください
          </p>
        )}
      </div>

      <span className="sr-only" role="status" aria-live="polite">{liveMessage}</span>

      {/* データ量サマリ */}
      <section className="border border-surface-line rounded-lg bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-surface-line bg-surface-sunken">
          <h2 className="text-h3 flex items-center gap-2"><Database size={16} aria-hidden /> 取込データ量（月次件数）</h2>
        </div>
        {/* min-w を与えないと w-full が優先され、狭い画面で横スクロールせずセルが潰れて
            「データ種別」が3行、店舗名が6行に折り返す (320px 実機確認で判明) */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] text-xs">
            <thead className="bg-surface-sunken text-text-muted">
              <tr>
                <th scope="col" className="relative text-left px-3 py-2 whitespace-nowrap">データ種別</th>
                <th scope="col" className="relative text-left px-3 py-2 whitespace-nowrap">店舗</th>
                {allMonths.slice(-12).map((m) => (
                  <th key={m} scope="col" className="relative text-right px-2 py-2 min-w-[70px] tabular-nums">{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {['sales', 'delivery_sales', 'delivery_details', 'labor', 'opex'].map((dt) => {
                const label = DATA_TYPE_SPECS[dt]?.label ?? dt;
                const storesInDt = summaryMatrix.get(dt);
                const used = [...(storesInDt?.keys() ?? [])];
                const storeCodes = stores.map((s) => s.store_code).filter((c) => used.includes(c));
                if (storeCodes.length === 0) {
                  return (
                    <tr key={dt} className="border-t border-surface-line">
                      <td className="px-3 py-1.5 font-medium">{label}</td>
                      <td colSpan={13} className="px-3 py-1.5 text-text-muted">— データなし —</td>
                    </tr>
                  );
                }
                return storeCodes.map((code, idx) => (
                  <tr key={`${dt}-${code}`} className="border-t border-surface-line hover:bg-surface-sunken/40">
                    {idx === 0 ? <td className="px-3 py-1.5 font-medium whitespace-nowrap" rowSpan={storeCodes.length}>{label}</td> : null}
                    <td className="px-3 py-1.5 max-w-[220px] truncate" title={stores.find((s) => s.store_code === code)?.store_name ?? code}>
                      {stores.find((s) => s.store_code === code)?.store_name ?? code}
                    </td>
                    {allMonths.slice(-12).map((m) => {
                      const cnt = storesInDt?.get(code)?.get(m) ?? 0;
                      return (
                        <td key={m} className={`text-right px-2 py-1.5 tabular-nums ${cnt > 0 ? 'text-text-body' : 'text-surface-line'}`}>
                          {cnt > 0 ? cnt.toLocaleString() : '−'}
                        </td>
                      );
                    })}
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* 絞込 */}
      <section className="border border-surface-line rounded-lg bg-white p-4 space-y-4">
        <h2 className="text-h3 flex items-center gap-2"><Filter size={16} aria-hidden /> 絞り込み</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <label className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1">データ種別</span>
            <Select value={dataType} onValueChange={setDataType}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {dataTypeList.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1">店舗</span>
            <Select value={storeId || '__all__'} onValueChange={(v) => setStoreId(v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">全店舗</SelectItem>
                {stores.map((s) => <SelectItem key={s.store_id} value={s.store_id}>{s.store_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1">期間のプリセット</span>
            <Select value={activePreset} onValueChange={applyPreset}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="期間を選ぶ" /></SelectTrigger>
              <SelectContent>
                {/* ⚠️ ここに36ヶ月を並べていたが、任意の月は下の日付欄で直接指定できるため冗長で、
                    かつ選択肢が長すぎて目的の月に辿り着けなかった。よく使う相対期間だけに絞る。 */}
                <SelectItem value="__all__">全期間</SelectItem>
                <SelectItem value="__thisMonth__">今月</SelectItem>
                <SelectItem value="__lastMonth__">先月</SelectItem>
                <SelectItem value="__last3__">直近3ヶ月</SelectItem>
                <SelectItem value="__last6__">直近6ヶ月</SelectItem>
                <SelectItem value="__last12__">直近12ヶ月</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <div className="block sm:col-span-2">
            <span className="block text-[12px] font-bold text-text-muted mb-1">期間（開始日 〜 終了日）</span>
            <div className="flex items-center gap-2">
              <input
                type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                aria-label="期間の開始日" max={to || undefined}
                className="flex-1 min-w-0 min-h-[44px] px-2 rounded-md border border-surface-line text-[13px] focus-ring"
              />
              <span aria-hidden className="text-text-muted shrink-0">〜</span>
              <input
                type="date" value={to} onChange={(e) => setTo(e.target.value)}
                aria-label="期間の終了日" min={from || undefined}
                className="flex-1 min-w-0 min-h-[44px] px-2 rounded-md border border-surface-line text-[13px] focus-ring"
              />
            </div>
          </div>
          <label className="block">
            <span className="block text-[12px] font-bold text-text-muted mb-1">検索</span>
            <div className="flex gap-2">
              <input
                type="search" value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setQ(searchInput); }}
                placeholder="名称・コードで絞り込み"
                className="flex-1 min-h-[44px] px-3 rounded-md border border-surface-line text-[13px] focus-ring"
              />
              <button type="button" onClick={() => setQ(searchInput)} aria-label="検索"
                className="min-h-[44px] min-w-[44px] px-3 rounded-md border border-surface-line hover:bg-surface-sunken focus-ring">
                <Search size={16} aria-hidden />
              </button>
            </div>
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={downloadCsv} disabled={total === 0}
            className="flex items-center gap-1.5 min-h-[44px] px-4 rounded-md border border-surface-line text-[13px] font-bold hover:bg-surface-sunken disabled:opacity-45 focus-ring">
            <Download size={15} aria-hidden /> CSVで書き出す（{total.toLocaleString()}件）
          </button>
          {isEditable && (
            <button type="button"
              onClick={() => { setModalError(null); setEditTarget({ mode: 'create', row: { store_id: storeId, ...defaultPeriodFields() } }); }}
              disabled={!storeId}
              title={!storeId ? '追加する店舗を選んでください' : undefined}
              className="flex items-center gap-1.5 min-h-[44px] px-4 rounded-md bg-brand-red hover:bg-brand-red-hover text-white text-[13px] font-bold btn-press-primary disabled:opacity-45 focus-ring">
              <Plus size={15} aria-hidden /> 行を追加
            </button>
          )}
          {selected.size > 0 && (
            <button type="button" onClick={() => setBulkConfirm(true)}
              className="flex items-center gap-1.5 min-h-[44px] px-4 rounded-md border border-surface-line text-[13px] font-bold hover:bg-surface-sunken focus-ring">
              <Trash2 size={15} aria-hidden /> 選択した{selected.size}件を削除
            </button>
          )}
        </div>
        {spec?.summary && <p className="text-xs text-text-muted">{spec.summary}</p>}
        {isEditable && (
          <p className="text-xs text-text-muted">
            この画面の編集は1行ずつ反映されます。月まとめて入れ替えたい場合は、取込画面からのCSV取込をご利用ください。
          </p>
        )}
      </section>

      {/* データ表 */}
      {records.isLoading && <p className="text-text-muted">読み込み中…</p>}
      {records.isError && (
        <p role="alert" className="p-3 rounded-md bg-alert-bg text-alert-text border border-alert-border">
          {(records.error as Error).message}
        </p>
      )}

      {records.data && (
        <section className="border border-surface-line rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-surface-line bg-surface-sunken flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-text-muted">
              現在の絞り込みで {total.toLocaleString()} 件
              {total > PAGE_SIZE && `（${page * PAGE_SIZE + 1}〜${Math.min((page + 1) * PAGE_SIZE, total)} 件を表示）`}
            </p>
            {pageCount > 1 && (
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                  aria-label="前のページ"
                  className="min-h-[44px] min-w-[44px] rounded-md border border-surface-line disabled:opacity-40 hover:bg-white focus-ring flex items-center justify-center">
                  <ChevronLeft size={16} aria-hidden />
                </button>
                <span className="text-[13px] tabular-nums px-2">{page + 1} / {pageCount}</span>
                <button type="button" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
                  aria-label="次のページ"
                  className="min-h-[44px] min-w-[44px] rounded-md border border-surface-line disabled:opacity-40 hover:bg-white focus-ring flex items-center justify-center">
                  <ChevronRight size={16} aria-hidden />
                </button>
              </div>
            )}
          </div>

          {total === 0 ? (
            <EmptyState message="該当するデータがありません" description="絞り込み条件を変えてお試しください" />
          ) : (
            <>
              {/* デスクトップ: 表 */}
              <div className="hidden md:block overflow-x-auto max-h-[560px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-surface-sunken text-text-muted sticky top-0 z-20">
                    <tr>
                      {isEditable && (
                        <th scope="col" className="relative sticky left-0 z-30 bg-surface-sunken px-3 py-2 w-11">
                          <label className="inline-flex items-center justify-center w-11 h-11 -m-3 cursor-pointer"><input ref={selectAllRef} type="checkbox" checked={allOnPageSelected}
                            onChange={toggleAllOnPage} aria-label="このページの行をすべて選択"
                            className="w-4 h-4 align-middle" /></label>
                        </th>
                      )}
                      {fields.map((f) => (
                        <th key={f.name} scope="col" className={`relative px-3 py-2 whitespace-nowrap font-bold ${f.numeric ? 'text-right' : 'text-left'}`}>
                          {f.label}
                        </th>
                      ))}
                      {isEditable && (
                        <th scope="col" className="relative sticky right-0 z-30 bg-surface-sunken px-3 py-2 text-right">操作</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const id = String(r.record_id ?? '');
                      const isHighlight = highlightId === id;
                      return (
                        <tr key={id}
                          className={`border-t border-surface-line hover:bg-surface-sunken/40 ${isHighlight ? 'bg-brand-yellow/10' : ''}`}>
                          {isEditable && (
                            <td className="sticky left-0 z-10 bg-white px-3 py-1.5">
                              <label className="inline-flex items-center justify-center w-11 h-11 -m-3 cursor-pointer"><input type="checkbox" checked={selected.has(id)}
                                onChange={() => setSelected((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(id)) next.delete(id); else next.add(id);
                                  return next;
                                })}
                                aria-label={`この行を選択`} className="w-4 h-4 align-middle" /></label>
                            </td>
                          )}
                          {fields.map((f) => (
                            <td key={f.name} className={`px-3 py-1.5 whitespace-nowrap ${f.numeric ? 'text-right tabular-nums' : ''}`}>
                              {formatCell(f, r[f.name])}
                            </td>
                          ))}
                          {isEditable && (
                            <td className="sticky right-0 z-10 bg-white px-2 py-1 text-right whitespace-nowrap">
                              <button type="button" aria-label="この行を修正"
                                onClick={() => { setModalError(null); setEditTarget({ mode: 'edit', row: r }); }}
                                className="inline-flex items-center justify-center w-11 h-11 rounded-md hover:bg-surface-sunken text-text-muted focus-ring">
                                <Pencil size={15} aria-hidden />
                              </button>
                              <button type="button" aria-label="この行を削除"
                                onClick={() => setDeleteTarget(r)}
                                className="inline-flex items-center justify-center w-11 h-11 rounded-md hover:bg-surface-sunken text-text-muted focus-ring">
                                <Trash2 size={15} aria-hidden />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* モバイル: カード */}
              <ul className="md:hidden divide-y divide-surface-line">
                {rows.map((r) => {
                  const id = String(r.record_id ?? '');
                  const layout = CARD_LAYOUT[dataType];
                  const primary = fields.find((f) => f.name === layout?.primary);
                  const secondary = (layout?.secondary ?? []).map((n) => fields.find((f) => f.name === n)).filter(Boolean);
                  const rest = fields.filter((f) => f.name !== layout?.primary && !(layout?.secondary ?? []).includes(f.name));
                  return (
                    <li key={id} className={`p-4 space-y-2 ${highlightId === id ? 'bg-brand-yellow/10' : ''}`}>
                      <div className="flex items-start gap-3">
                        {isEditable && (
                          <label className="inline-flex items-center justify-center w-11 h-11 -m-3 cursor-pointer"><input type="checkbox" checked={selected.has(id)}
                            onChange={() => setSelected((prev) => {
                              const next = new Set(prev);
                              if (next.has(id)) next.delete(id); else next.add(id);
                              return next;
                            })}
                            aria-label="この行を選択" className="w-5 h-5 mt-0.5 shrink-0" /></label>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[14px] break-words">
                            {primary ? formatCell(primary, r[primary.name]) : id}
                          </p>
                          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[13px] text-text-muted mt-0.5">
                            {secondary.map((f) => f && (
                              <span key={f.name}>{f.label}: <span className="text-text-body">{formatCell(f, r[f.name])}</span></span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <details className="text-[13px]">
                        <summary className="cursor-pointer text-text-muted min-h-[44px] flex items-center">すべての項目を表示</summary>
                        <dl className="mt-1 space-y-0.5">
                          {rest.map((f) => (
                            <div key={f.name} className="flex gap-2">
                              <dt className="text-text-muted shrink-0">{f.label}:</dt>
                              <dd className="break-words">{formatCell(f, r[f.name])}</dd>
                            </div>
                          ))}
                        </dl>
                      </details>
                      {isEditable && (
                        <div className="flex gap-2">
                          <button type="button" onClick={() => { setModalError(null); setEditTarget({ mode: 'edit', row: r }); }}
                            className="flex-1 min-h-[44px] rounded-md border border-surface-line text-[13px] font-bold hover:bg-surface-sunken focus-ring">
                            修正
                          </button>
                          <button type="button" onClick={() => setDeleteTarget(r)}
                            className="flex-1 min-h-[44px] rounded-md border border-surface-line text-[13px] font-bold hover:bg-surface-sunken focus-ring">
                            削除
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>
      )}

      {editTarget && (
        <RecordEditModal
          open
          mode={editTarget.mode}
          dataType={dataType}
          storeLabel={editTarget.mode === 'edit' ? String(editTarget.row.store_code ?? storeLabel) : storeLabel}
          initial={editTarget.row}
          impactedPeriods={
            // 変更後の期間はモーダルが編集中の値から算出する (§8-1 / R-10)。
            // ここでは編集前の行が属する期間だけを渡し、月跨ぎ修正で前後2ヶ月が並ぶようにする。
            [periodOfRow(dataType, editTarget.row)].filter((v): v is string => !!v)
          }
          saving={saving}
          errorMessage={modalError}
          onSubmit={submitRecord}
          onClose={() => setEditTarget(null)}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="この行を削除しますか？"
        destructive
        loading={saving}
        confirmLabel="削除する"
        message={(
          <div className="space-y-3 text-left">
            {/* 対象行の識別情報を必ず出す。「取り消せません」だけでは、どの行を消そうとしているのか
                確認できないまま実行することになる (S-11 Design レビュー指摘) */}
            {deleteTarget && (
              <dl className="rounded-md border border-surface-line bg-surface-sunken p-3 text-[13px] space-y-1">
                {fields.filter((f) => DELETE_CONFIRM_FIELDS.includes(f.name)).map((f) => (
                  <div key={f.name} className="flex gap-2">
                    <dt className="text-text-muted shrink-0">{f.label}</dt>
                    <dd className="font-bold break-all">{formatCell(f, deleteTarget[f.name])}</dd>
                  </div>
                ))}
              </dl>
            )}
            <p>この操作は取り消せません。</p>
            <PublishImpactWarning periods={deleteTarget ? [periodOfRow(dataType, deleteTarget)].filter((v): v is string => !!v) : []} />
          </div>
        )}
        onConfirm={doDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirm}
        title={`選択した ${selected.size} 件を削除しますか？`}
        destructive
        loading={saving}
        confirmLabel="まとめて削除する"
        message={(
          <div className="space-y-3 text-left">
            <p>この操作は取り消せません。</p>
            {selectedRowsOnPage.length > 0 && (
              <div className="rounded-md border border-surface-line bg-surface-sunken p-2 text-[13px]">
                <p className="font-bold mb-1">削除対象（表示中のページ分）</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  {selectedRowsOnPage.slice(0, 5).map((r) => {
                    const layout = CARD_LAYOUT[dataType];
                    const primary = fields.find((f) => f.name === layout?.primary);
                    return <li key={String(r.record_id)}>{primary ? formatCell(primary, r[primary.name]) : String(r.record_id)}</li>;
                  })}
                  {selected.size > selectedRowsOnPage.slice(0, 5).length && (
                    <li className="list-none text-text-muted">ほか {selected.size - Math.min(5, selectedRowsOnPage.length)} 件</li>
                  )}
                </ul>
              </div>
            )}
            <PublishImpactWarning periods={bulkPeriods} />
          </div>
        )}
        onConfirm={doBulkDelete}
        onClose={() => setBulkConfirm(false)}
      />
    </div>
  );
}
